import { api } from "../api.ts";
import { BRAND, icon } from "../icons.ts";
import { euros, relative, time } from "../format.ts";
import { $, $$, empty, errorText, esc, load, skeleton, toast } from "../ui.ts";
import type { Attachment, Confirmation, Message } from "../types.ts";
import { renderReply } from "../markdown.ts";
import type { ViewContext } from "../main.ts";
import { startTour, tourOffer } from "../tour.ts";
import { speech } from "../speech.ts";

const SUGGESTIONS = ["Buenos días, ¿qué hay hoy?", "Prepárame la reunión de la operación de Lisboa", "Organiza el viaje a Lisboa para ver a Rui",
  "Reserva en Lúa para cuatro el viernes a las nueve", "Resérvame en la web de Mesa Libre en Brasa Norte para 4 el viernes a las 21:00", "Sácame un billete de tren a Barcelona para el viernes por la mañana", "Laura pide mover la reunión del jueves; respóndele que el viernes a la misma hora", "¿Hay algo raro en el correo?"];

const mmss = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.round(ms / 1000) % 60).padStart(2, "0")}`;
const wave = (seed: string, n = 32) => Array.from({ length: n }, (_, i) => `<i style="height:${Math.round(4 + Math.abs(Math.sin((seed.length + i) * 1.7) * Math.cos(i * 0.6)) * 20)}px"></i>`).join("");


export function confirmationCard(c: Confirmation, now = new Date()): string {
  const done: Record<string, [string, string]> = { approved: ["check", "Confirmado"], rejected: ["x", "Rechazado"], expired: ["clock", "Caducada sin respuesta"] };
  const pending = c.status === "pending";
  const minutes = Math.max(0, Math.ceil((new Date(c.expiresAt).getTime() - now.getTime()) / 60000));
  return `
    <article class="confirmation ${c.status}" data-confirmation="${c.id}" aria-label="Confirmación: ${esc(c.summary)}">
      <header>${icon(pending ? "circle-alert" : "shield-check", "sm")}<span>${pending ? "Necesita tu confirmación" : "Confirmación"}</span>
        <span class="expires">${pending ? `caduca en ${minutes} min` : esc(relative(c.decidedAt ?? c.createdAt, now))}</span></header>
      <div class="body">
        <p class="summary">${esc(c.summary)}${pending ? "; ¿confirmo?" : ""}</p>
        <dl>${c.details.map((d) => `<dt>${esc(d.label)}</dt><dd>${esc(d.value)}</dd>`).join("")}</dl>
        ${c.reason ? `<p class="why">${icon("shield", "sm")}<span>${esc(c.reason)}</span></p>` : ""}
        ${pending ? `<div class="actions">
            <button class="btn primary" type="button" data-decide="approve">${icon("check", "sm")} Confirmar${c.amountCents ? ` ${esc(euros(c.amountCents))}` : ""}</button>
            <button class="btn" type="button" data-decide="reject">${icon("x", "sm")} No, déjalo</button></div>`
          : `<p class="decided ${c.status}">${icon(done[c.status]![0], "sm")} ${done[c.status]![1]}</p>`}
      </div>
    </article>`;
}

/** Wires the buttons of every pending card inside `root`. */
export function wireConfirmations(root: ParentNode, onDecided?: (c: Confirmation) => void) {
  $$(root, ".confirmation.pending").forEach((card) => {
    $$<HTMLButtonElement>(card, "[data-decide]").forEach((b) => b.addEventListener("click", async () => {
      const decision = b.dataset.decide as "approve" | "reject";
      $$<HTMLButtonElement>(card, "[data-decide]").forEach((x) => { x.disabled = true; });
      b.innerHTML = `${icon("refresh-cw", "sm spin")} ${decision === "approve" ? "Confirmando…" : "Rechazando…"}`;
      try {
        const c = await api.decide(card.dataset.confirmation!, decision);
        card.outerHTML = confirmationCard(c);
        onDecided?.(c);
      } catch (err) {
        toast(errorText(err), "error");
        $$<HTMLButtonElement>(card, "[data-decide]").forEach((x) => { x.disabled = false; });
        b.innerHTML = decision === "approve" ? `${icon("check", "sm")} Confirmar` : `${icon("x", "sm")} No, déjalo`;
      }
    }));
  });
}

const kb = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`);
/** Photos as thumbnails (open full size), anything else as a download chip. */
function attachmentsHtml(list: Attachment[] | undefined): string {
  if (!list?.length) return "";
  return `<div class="attachments">${list.map((a) => a.mime.startsWith("image/")
    ? `<a class="thumb" href="/api/v1/files/${a.id}" target="_blank" rel="noopener" aria-label="Abrir la foto ${esc(a.name)}"><img src="/api/v1/files/${a.id}" alt="${esc(a.name)}" loading="lazy"></a>`
    : `<a class="file-chip" href="/api/v1/files/${a.id}" download="${esc(a.name)}">${icon(a.mime === "application/pdf" ? "file-text" : "file", "sm")}<span class="name">${esc(a.name)}</span><span class="size">${kb(a.size)}</span>${icon("download", "sm")}</a>`).join("")}</div>`;
}

function messageHtml(m: Message, confirmations: Map<string, Confirmation>): string {
  if (m.kind === "confirmation" && m.confirmationId) {
    const c = confirmations.get(m.confirmationId);
    return c ? `<div class="msg assistant" data-id="${m.id}">${confirmationCard(c)}</div>` : "";
  }
  if (m.author === "user" && m.kind === "voice" && m.voice) {
    return `<div class="msg user" data-id="${m.id}"><div class="bubble voice">
      <div class="player"><span class="voice-tag">${icon("mic", "sm")}</span><span class="wave" aria-hidden="true">${wave(m.id)}</span><span class="duration num">${mmss(m.voice.durationMs)}</span></div>
      <p class="transcript"><span class="sr-only">Nota de voz transcrita: </span>${esc(m.voice.transcript ?? m.text ?? "")}</p></div><span class="meta">${time(m.createdAt)}</span></div>`;
  }
  if (m.author === "user") return `<div class="msg user" data-id="${m.id}"><div class="bubble">${attachmentsHtml(m.attachments)}${esc(m.text)}</div><span class="meta">${time(m.createdAt)}</span></div>`;
  if (m.author === "system") {
    // Vera starting work on her own (a new WhatsApp, a scheduled task) is news, not an error.
    const kind = /^WhatsApp de /.test(m.text ?? "") ? "message-square" : /^Encargo programado/.test(m.text ?? "") ? "clock" : null;
    return kind ? `<div class="msg assistant" data-id="${m.id}"><div class="notice-box" role="status">${icon(kind, "sm")}<span>${esc(m.text)}</span></div></div>`
      : `<div class="msg assistant" data-id="${m.id}"><div class="error-box" role="status">${icon("circle-alert", "sm")}<span>${esc(m.text)}</span></div></div>`;
  }
  return `<div class="msg assistant" data-id="${m.id}"><span class="assistant-sign">${BRAND} Vera · ${time(m.createdAt)}${speech.supported() ? `<button class="btn ghost icon-only speak" type="button" data-speak aria-label="Escuchar la respuesta" aria-pressed="false">${icon("volume-2", "sm")}</button>` : ""}</span><div class="text">${renderReply(m.text ?? "")}</div>${attachmentsHtml(m.attachments)}</div>`;
}

export async function mount(main: HTMLElement, ctx: ViewContext) {
  const voiceOn = !!ctx.health?.voice;
  main.innerHTML = `<div class="content chat">
      <section class="thread" aria-label="Conversación" aria-live="polite" aria-relevant="additions" data-thread></section>
      <div class="composer">
        <div class="pending-files" data-pending hidden aria-label="Adjuntos para el próximo mensaje"></div>
        <div class="suggestions" aria-label="Sugerencias">${SUGGESTIONS.map((s) => `<button type="button" data-suggestion>${esc(s)}</button>`).join("")}</div>
        <form data-form aria-label="Escribir a Vera">
          <label class="sr-only" for="input">Mensaje para Vera</label>
          <input type="file" data-file hidden accept="image/jpeg,image/png,image/webp,image/gif,application/pdf" multiple>
          <button class="btn ghost icon-only" type="button" data-attach data-text aria-label="Adjuntar foto o PDF">${icon("paperclip")}</button>
          <textarea id="input" name="text" rows="1" placeholder="Escribe${voiceOn ? " o graba una nota" : " a Vera"}…" autocomplete="off" data-text></textarea>
          ${voiceOn ? `<button class="btn ghost icon-only" type="button" data-mic data-text aria-label="Grabar nota de voz">${icon("mic")}</button>` : ""}
          <button class="btn primary send" type="submit" data-text aria-label="Enviar">${icon("arrow-up")}</button>
          <div class="recording" role="status" data-rec hidden><span class="dot" aria-hidden="true"></span><span class="num" data-clock>0:00</span>
            <span class="live-wave" aria-hidden="true">${"<i></i>".repeat(40)}</span><span class="sr-only">Grabando nota de voz</span></div>
          <button class="btn ghost icon-only" type="button" data-cancel data-rec hidden aria-label="Cancelar grabación">${icon("x")}</button>
          <button class="btn primary send" type="button" data-stop data-rec hidden aria-label="Enviar nota de voz">${icon("send", "sm")}</button>
        </form>
      </div>
    </div>`;
  const thread = $(main, "[data-thread]");
  const form = $<HTMLFormElement>(main, "[data-form]");
  const input = $<HTMLTextAreaElement>(main, "#input");
  const confirmations = new Map<string, Confirmation>();
  const seen = new Set<string>();
  /** Reads one reply aloud; its button shows it is playing and stops it. */
  const speak = (msg: Element) => {
    const button = msg.querySelector<HTMLButtonElement>("[data-speak]");
    const playing = button?.getAttribute("aria-pressed") === "true";
    speech.stop();
    $$(thread, '[data-speak][aria-pressed="true"]').forEach((b) => b.setAttribute("aria-pressed", "false"));
    if (playing || !button) return;
    button.setAttribute("aria-pressed", "true");
    speech.say(msg.querySelector(".text")?.textContent ?? "", () => button.setAttribute("aria-pressed", "false"));
  };
  form.addEventListener("pointerdown", (e) => { if ((e.target as Element).closest("[data-mic], .send")) speech.unlock(); });
  thread.addEventListener("click", (e) => {
    const b = (e.target as Element).closest("[data-speak]");
    if (b) speak(b.closest(".msg")!);
  });
  window.addEventListener("hashchange", () => speech.stop(), { once: true });
  let lastUserVoice = false; // «when I speak to it»: answer aloud only after a voice note
  const toBottom = () => requestAnimationFrame(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }));
  let conversationId = "";
  let typing: HTMLElement | null = null;
  // What the assistant's browser shows, live: one window per task, kept in the thread when the task ends.
  let live: { taskId: string; el: HTMLElement } | null = null;
  const showLive = (d: { taskId: string; activity: string; url: string; screenshot: string }) => {
    if (live?.taskId !== d.taskId) {
      const el = document.createElement("div");
      el.className = "msg assistant";
      el.innerHTML = `<figure class="webview" data-live>
        <div class="webview-bar">${icon("lock", "sm")}<a class="webview-url" target="_blank" rel="noopener" data-url></a></div>
        <img data-shot alt="" width="1100" height="760">
        <figcaption><span class="live-dot" aria-hidden="true"></span><span data-caption></span></figcaption></figure>`;
      (typing ? typing.before.bind(typing) : thread.append.bind(thread))(el);
      live = { taskId: d.taskId, el };
    }
    const url = new URL(d.url, location.href);
    const link = $<HTMLAnchorElement>(live.el, "[data-url]");
    link.href = url.origin === location.origin ? url.pathname + url.search : d.url;
    link.textContent = `${url.host}${url.pathname}${url.search}`;
    link.setAttribute("aria-label", `Abrir en otra pestaña: ${url.pathname}`);
    const shot = $<HTMLImageElement>(live.el, "[data-shot]");
    shot.src = d.screenshot;
    shot.alt = `Lo que ve el navegador de Vera: ${d.activity}`;
    $(live.el, "[data-caption]").textContent = `Navegador de Vera · ${d.activity}`;
    live.el.querySelector(".webview")!.classList.remove("done");
    toBottom();
  };
  const endLive = () => {
    if (!live) return;
    live.el.querySelector(".webview")!.classList.add("done");
    $(live.el, "[data-caption]").textContent = "Navegador de Vera · última página vista";
    live = null;
  };
  // The user may type before the conversation is loaded; sending waits for it instead of losing the message.
  let markReady: (id: string) => void = () => {};
  const ready = new Promise<string>((resolve) => { markReady = resolve; });

  const showTyping = (on: boolean) => {
    typing?.remove(); typing = null;
    if (!on) return;
    typing = document.createElement("div");
    typing.className = "msg assistant";
    typing.innerHTML = `<span class="assistant-sign">${BRAND} Vera <span data-activity></span></span><span class="typing" aria-hidden="true"><i></i><i></i><i></i></span><span class="sr-only" role="status" data-activity-sr>Vera está trabajando</span>`;
    thread.append(typing);
    toBottom();
  };
  let received = 0; // assistant messages seen, to know whether a reply beat the send request back
  const append = (m: Message) => {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    if (m.author !== "user") received++;
    thread.querySelector(".empty")?.remove();
    const holder = document.createElement("div");
    holder.innerHTML = messageHtml(m, confirmations);
    wireConfirmations(holder, () => void ctx.refreshNav());
    (typing ? typing.before.bind(typing) : thread.append.bind(thread))(...holder.childNodes);
    toBottom();
  };

  // Files go up as soon as they are picked; the message carries their ids.
  type Pending = { key: string; name: string; preview: string | null; id?: string; failed?: boolean };
  let pending: Pending[] = [];
  const pendingBox = $(main, "[data-pending]");
  const fileInput = $<HTMLInputElement>(main, "[data-file]");
  const renderPending = () => {
    pendingBox.hidden = !pending.length;
    pendingBox.innerHTML = pending.map((p) => `<span class="pending-file${p.failed ? " failed" : ""}${p.id ? "" : " uploading"}">
      ${p.preview ? `<img src="${p.preview}" alt="">` : icon("file-text", "sm")}<span class="name">${esc(p.name)}</span>
      ${p.failed ? `<span class="sr-only">no se ha podido subir</span>` : p.id ? "" : `${icon("refresh-cw", "sm spin")}<span class="sr-only">subiendo</span>`}
      <button type="button" class="btn ghost icon-only" data-unpick="${p.key}" aria-label="Quitar ${esc(p.name)}">${icon("x", "sm")}</button></span>`).join("");
    $$<HTMLButtonElement>(pendingBox, "[data-unpick]").forEach((b) => b.addEventListener("click", () => { pending = pending.filter((p) => p.key !== b.dataset.unpick); renderPending(); input.focus(); }));
  };
  $(main, "[data-attach]").addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", async () => {
    const files = [...(fileInput.files ?? [])].slice(0, 4 - pending.length);
    fileInput.value = "";
    for (const file of files) {
      if (file.size > 8 * 1024 * 1024) { toast(`${file.name} pesa más de 8 MB.`, "error"); continue; }
      const p: Pending = { key: crypto.randomUUID(), name: file.name, preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null };
      pending.push(p); renderPending();
      try { p.id = (await api.upload(await ready, file)).id; }
      catch (err) { p.failed = true; toast(errorText(err), "error"); }
      renderPending();
    }
    input.focus();
  });

  async function send(text: string) {
    if (pending.some((p) => !p.id && !p.failed)) return toast("Espera a que terminen de subir los adjuntos.");
    const files = pending.filter((p) => p.id).map((p) => p.id!);
    const restore = input.value;
    input.value = ""; input.style.height = "";
    const before = received;
    try {
      await api.send(await ready, text, files);
      pending = []; renderPending();
      if (received === before) showTyping(true);
      setTimeout(() => void sync(), 1500);
    } catch (err) {
      input.value = restore || text;
      thread.insertAdjacentHTML("beforeend", `<div class="msg assistant"><div class="error-box" role="alert">${icon("circle-alert", "sm")}<span><strong>No se ha enviado.</strong> ${esc(errorText(err))}</span></div></div>`);
      toBottom();
    }
  }
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const t = input.value.trim() || (pending.some((p) => p.id) ? "Te mando esto." : "");
    if (t) void send(t); else input.focus();
  });
  input.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); form.requestSubmit(); } });
  input.addEventListener("input", () => { input.style.height = "auto"; input.style.height = `${Math.min(input.scrollHeight, 160)}px`; });
  $$<HTMLButtonElement>(main, "[data-suggestion]").forEach((b) => b.addEventListener("click", () => void send(b.textContent ?? "")));

  await load(thread, {
    placeholder: `<div style="display:grid;gap:18px">${skeleton(2)}${skeleton(1, true)}${skeleton(3)}</div>`,
    fetch: async () => {
      const list = await api.conversations();
      conversationId = list.items[0]?.id ?? (await api.createConversation()).id;
      return api.messages(conversationId);
    },
    render: (page) => {
      page.confirmations.forEach((c) => confirmations.set(c.id, c));
      thread.innerHTML = page.items.length ? "" : empty("message-square", "Empieza cuando quieras",
        "Pídele algo como se lo pedirías a una persona: «¿qué tengo mañana?», «reserva para cuatro el viernes». Antes de pagar, reservar o escribir a nadie te pedirá confirmación.");
      page.items.forEach(append);
      if (page.items.at(-1)?.author === "user") showTyping(true);
    },
  });
  if (!conversationId) return;
  markReady(conversationId);

  /** Fills any gap the event stream missed (sent before it connected, or during a reconnect). */
  async function sync() {
    try {
      const page = await api.messages(conversationId);
      page.confirmations.forEach((c) => {
        const known = confirmations.get(c.id);
        confirmations.set(c.id, c);
        const card = thread.querySelector(`[data-confirmation="${c.id}"]`);
        if (card && known && known.status !== c.status) { card.outerHTML = confirmationCard(c); wireConfirmations(thread, () => void ctx.refreshNav()); }
      });
      const unseen = page.items.filter((m) => !seen.has(m.id));
      unseen.forEach(append);
      const last = page.items.at(-1);
      if (unseen.length) showTyping(last?.author === "user" || (last?.kind === "confirmation" && confirmations.get(last.confirmationId ?? "")?.status === "approved"));
    } catch { /* the next event or sync will catch up */ }
  }
  const events = new EventSource(`/api/v1/conversations/${conversationId}/events`);
  events.addEventListener("open", () => void sync());
  events.addEventListener("message", (e) => {
    const { data } = JSON.parse((e as MessageEvent).data) as { data: { message: Message; confirmation?: Confirmation } };
    if (data.confirmation) confirmations.set(data.confirmation.id, data.confirmation);
    if (data.message.author === "user") { lastUserVoice = data.message.kind === "voice"; append(data.message); showTyping(true); return; }
    showTyping(false); // a confirmation card waits for the user; a text message ends the turn
    if (data.message.kind !== "confirmation") endLive();
    const isNew = !seen.has(data.message.id);
    append(data.message);
    const mode = speech.mode();
    if (isNew && data.message.author === "assistant" && data.message.kind === "text" && (mode === "always" || (mode === "voice" && lastUserVoice))) {
      const el = thread.querySelector(`[data-id="${data.message.id}"]`);
      if (el) speak(el);
    }
    void ctx.refreshNav();
  });
  events.addEventListener("progress", (e) => {
    const { data } = JSON.parse((e as MessageEvent).data) as { data: { taskId: string; activity: string; url?: string; screenshot?: string } };
    if (!typing) showTyping(true);
    if (data.url && data.screenshot) showLive({ taskId: data.taskId, activity: data.activity, url: data.url, screenshot: data.screenshot });
    const label = `· ${data.activity}…`;
    typing!.querySelector("[data-activity]")!.textContent = label;
    typing!.querySelector("[data-activity-sr]")!.textContent = `Vera está ${data.activity}`;
  });
  events.addEventListener("confirmation", (e) => {
    const { data } = JSON.parse((e as MessageEvent).data) as { data: Confirmation };
    confirmations.set(data.id, data);
    const card = thread.querySelector(`[data-confirmation="${data.id}"]`);
    if (card && !card.classList.contains(data.status)) { card.outerHTML = confirmationCard(data); wireConfirmations(thread, () => void ctx.refreshNav()); }
    if (data.status === "approved") showTyping(true);
  });
  window.addEventListener("hashchange", () => events.close(), { once: true });


  const sharedText = sessionStorage.getItem("vera.share");
  if (sharedText) {
    sessionStorage.removeItem("vera.share");
    input.value = `Me ha llegado esto. Organízalo como mi secretaria (agenda y respuesta sugerida, sin enviar nada):\n«${sharedText}»`;
    input.dispatchEvent(new Event("input"));
    input.focus();
  }

  if (voiceOn) wireVoice(form, conversationId, (on) => showTyping(on));

  const prefs = await api.preferences().catch(() => null);
  if (prefs && !prefs.tutorialDone) {
    thread.insertAdjacentHTML("afterbegin", tourOffer());
    const offer = $(thread, ".tour-offer");
    $(offer, "[data-tour-start]").addEventListener("click", () => { offer.remove(); startTour({ isAdmin: ctx.me.user.role === "admin" }); });
    $(offer, "[data-tour-dismiss]").addEventListener("click", () => { offer.remove(); void api.updatePreferences({ tutorialDone: true }).catch(() => {}); });
  }
}

/** Records with MediaRecorder and uploads the note; the server transcribes it. */
function wireVoice(form: HTMLFormElement, conversationId: string, typing: (on: boolean) => void) {
  const toggle = (rec: boolean) => {
    $$(form, "[data-text]").forEach((el) => { el.hidden = rec; });
    $$(form, "[data-rec]").forEach((el) => { el.hidden = !rec; });
  };
  const bars = $$(form, ".live-wave i");
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let started = 0;
  let timer = 0;
  let cancelled = false;
  const stopStream = () => recorder?.stream.getTracks().forEach((t) => t.stop());

  $(form, "[data-mic]").addEventListener("click", async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorder = new MediaRecorder(stream);
    } catch {
      toast("No hay acceso al micrófono. Permítelo en el navegador o escribe el mensaje.", "error");
      return;
    }
    chunks = []; cancelled = false; started = performance.now();
    recorder.ondataavailable = (e) => chunks.push(e.data);
    recorder.onstop = async () => {
      clearInterval(timer); stopStream(); toggle(false);
      if (cancelled) return;
      const blob = new Blob(chunks, { type: recorder!.mimeType || "audio/webm" });
      typing(true);
      try { await api.sendVoice(conversationId, blob, performance.now() - started); }
      catch (err) { typing(false); toast(errorText(err), "error"); }
    };
    recorder.start();
    toggle(true);
    $<HTMLButtonElement>(form, "[data-stop]").focus();
    timer = window.setInterval(() => {
      const ms = performance.now() - started;
      $(form, "[data-clock]").textContent = mmss(ms);
      bars.forEach((b, i) => { b.style.height = `${4 + Math.abs(Math.sin(ms / 180 + i)) * 16}px`; });
      if (ms > 120_000) recorder?.stop();
    }, 100);
  });
  $(form, "[data-stop]").addEventListener("click", () => recorder?.stop());
  $(form, "[data-cancel]").addEventListener("click", () => { cancelled = true; recorder?.stop(); });
}
